import type { Ref } from 'vue'

import type {
  ApprovalStage,
  WorkflowStageAssistantAction,
  WorkflowStageAssistantField,
  WorkflowStageAssistantFieldOption,
} from '@/types/workflow'

import type { ConsistencyAssetDraft, ConsistencyAssetType, StoryboardSceneDraft } from './types'
import type { StageAssistantApplySummary, StageAssistantRegistry } from './stageAssistantTypes'

type StoryboardWorkspaceTab = 'script' | 'consistency' | 'generation'
type StoryboardGenerationTab = 'image' | 'video'

interface SelectOption {
  label: string
  value: string
}

interface StageAssistantFieldBinding {
  field: WorkflowStageAssistantField
  apply: (value: WorkflowStageAssistantAction['value']) => boolean
}

interface BuildStageAssistantRegistryOptions {
  currentStage: ApprovalStage
  currentStageLabel: string
  instruction?: string
  editorContent: Ref<string>
  reviewFeedback: Ref<string>
  markEditorDirty: () => void
  storyboardWorkspaceTab: Ref<StoryboardWorkspaceTab>
  storyboardGenerationTab: Ref<StoryboardGenerationTab>
  consistencyAssetTab: Ref<ConsistencyAssetType>
  selectedStoryboardSceneNumber: Ref<number | null>
  storyboardEditingSceneNumber: Ref<number | null>
  storyboardDraftScenes: Ref<StoryboardSceneDraft[]>
  markStoryboardDraftDirty: () => void
  consistencyAssets: Ref<Record<ConsistencyAssetType, ConsistencyAssetDraft[]>>
  markConsistencyDirty: () => void
  finalVoiceoverMode: Ref<string>
  finalVoiceoverTone: Ref<string>
  finalBgmPlan: Ref<string>
  finalVoiceoverDraft: Ref<string>
  finalTtsScript: Ref<string>
  finalVoiceoverModeOptions: ReadonlyArray<SelectOption>
  finalVoiceoverToneOptions: ReadonlyArray<SelectOption>
  finalBgmPlanOptions: ReadonlyArray<SelectOption>
}

function stringifyFieldValue(value: WorkflowStageAssistantAction['value']) {
  if (typeof value === 'string') {
    return value.trim()
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  if (Array.isArray(value)) {
    return value.join('、').trim()
  }
  return ''
}

function normalizeSelectValue(value: WorkflowStageAssistantAction['value'], options: WorkflowStageAssistantFieldOption[]) {
  const raw = stringifyFieldValue(value)
  if (!raw) {
    return ''
  }

  const direct = options.find((option) => option.value === raw)
  if (direct) {
    return direct.value
  }

  const byLabel = options.find((option) => option.label === raw)
  return byLabel?.value || ''
}

function normalizeNumberValue(value: WorkflowStageAssistantAction['value']) {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) {
    return null
  }
  return numeric
}

function buildSelectField(
  key: string,
  label: string,
  currentValue: string,
  options: ReadonlyArray<SelectOption>,
  apply: (value: string) => void,
  description?: string,
): StageAssistantFieldBinding {
  const normalizedOptions = options.map((option) => ({ label: option.label, value: option.value }))
  return {
    field: {
      key,
      label,
      type: 'select',
      current_value: currentValue,
      description,
      options: normalizedOptions,
    },
    apply: (value) => {
      const nextValue = normalizeSelectValue(value, normalizedOptions)
      if (!nextValue) {
        return false
      }
      apply(nextValue)
      return true
    },
  }
}

function buildTextField(
  key: string,
  label: string,
  currentValue: string,
  apply: (value: string) => void,
  description?: string,
  type: 'text' | 'textarea' = 'textarea',
): StageAssistantFieldBinding {
  return {
    field: {
      key,
      label,
      type,
      current_value: currentValue,
      description,
    },
    apply: (value) => {
      const nextValue = stringifyFieldValue(value)
      apply(nextValue)
      return true
    },
  }
}

function buildNumberField(
  key: string,
  label: string,
  currentValue: number,
  apply: (value: number) => void,
  description?: string,
): StageAssistantFieldBinding {
  return {
    field: {
      key,
      label,
      type: 'number',
      current_value: currentValue,
      description,
    },
    apply: (value) => {
      const nextValue = normalizeNumberValue(value)
      if (nextValue === null) {
        return false
      }
      apply(nextValue)
      return true
    },
  }
}

function getAssetByKey(
  assets: Record<ConsistencyAssetType, ConsistencyAssetDraft[]>,
  assetType: ConsistencyAssetType,
  assetId: string,
) {
  return assets[assetType].find((item) => item.id === assetId)
}

function getStoryboardScene(scenes: StoryboardSceneDraft[], sceneNumber: number) {
  return scenes.find((scene) => scene.scene_number === sceneNumber)
}

function getRelevantStoryboardScenes(options: BuildStageAssistantRegistryOptions) {
  const orderedScenes = [...options.storyboardDraftScenes.value].sort((left, right) => left.scene_number - right.scene_number)
  if (!orderedScenes.length) {
    return []
  }

  const mentionedSceneNumbers = Array.from(new Set(Array.from(
    String(options.instruction || '').matchAll(/(?:镜头|场景)\s*(\d{1,3})/g),
    (match) => Number(match[1]),
  ).filter((value) => Number.isFinite(value) && value > 0)))
  if (mentionedSceneNumbers.length) {
    return orderedScenes.filter((scene) => mentionedSceneNumbers.includes(scene.scene_number)).slice(0, 4)
  }

  const primarySceneNumber = options.storyboardEditingSceneNumber.value
    || options.selectedStoryboardSceneNumber.value
    || orderedScenes[0].scene_number
  const primaryIndex = Math.max(orderedScenes.findIndex((scene) => scene.scene_number === primarySceneNumber), 0)
  const relevantSceneNumbers = new Set<number>([orderedScenes[primaryIndex].scene_number])

  if (options.storyboardWorkspaceTab.value === 'script') {
    const previousScene = orderedScenes[primaryIndex - 1]
    const nextScene = orderedScenes[primaryIndex + 1]
    if (previousScene) {
      relevantSceneNumbers.add(previousScene.scene_number)
    }
    if (nextScene) {
      relevantSceneNumbers.add(nextScene.scene_number)
    }

    if (!options.selectedStoryboardSceneNumber.value && orderedScenes[primaryIndex + 2]) {
      relevantSceneNumbers.add(orderedScenes[primaryIndex + 2].scene_number)
    }
  }

  return orderedScenes.filter((scene) => relevantSceneNumbers.has(scene.scene_number)).slice(0, 4)
}

function getRelevantConsistencyAssets(options: BuildStageAssistantRegistryOptions) {
  const assets = [...options.consistencyAssets.value[options.consistencyAssetTab.value]]
  if (!assets.length) {
    return []
  }

  const selectedSceneNumber = options.selectedStoryboardSceneNumber.value
  const prioritized = selectedSceneNumber
    ? assets.filter((asset) => asset.linked_scene_numbers.includes(selectedSceneNumber))
    : []
  const remainder = assets.filter((asset) => !prioritized.includes(asset))

  return [...prioritized, ...remainder].slice(0, 6)
}

export function buildStageAssistantRegistry(options: BuildStageAssistantRegistryOptions): StageAssistantRegistry {
  const bindings = new Map<string, StageAssistantFieldBinding>()

  function register(binding: StageAssistantFieldBinding) {
    bindings.set(binding.field.key, binding)
  }

  if (options.currentStage === 'proposal' || options.currentStage === 'script') {
    register(buildTextField(
      'stage.primary_content',
      `${options.currentStageLabel}正文`,
      options.editorContent.value,
      (value) => {
        options.editorContent.value = value
        options.markEditorDirty()
      },
      '当前阶段主编辑器正文',
    ))

    return {
      stage: options.currentStage,
      stageLabel: options.currentStageLabel,
      currentFocus: `${options.currentStageLabel}正文编辑区，优先直接修改正文内容而不是审批意见`,
      fields: Array.from(bindings.values()).map((item) => item.field),
      applyActions: (actions) => applyStageAssistantActions(bindings, actions),
    }
  }

  if (options.currentStage === 'final') {
    register(buildSelectField(
      'final.voiceover_mode',
      '终版配音方案',
      options.finalVoiceoverMode.value,
      options.finalVoiceoverModeOptions,
      (value) => {
        options.finalVoiceoverMode.value = value
      },
      '决定是否保留当前音轨、走 AI 配音或人工终混',
    ))
    register(buildSelectField(
      'final.voiceover_tone',
      '终版旁白语气',
      options.finalVoiceoverTone.value,
      options.finalVoiceoverToneOptions,
      (value) => {
        options.finalVoiceoverTone.value = value
      },
      '最终旁白的语气风格',
    ))
    register(buildSelectField(
      'final.bgm_plan',
      '终版 BGM 方案',
      options.finalBgmPlan.value,
      options.finalBgmPlanOptions,
      (value) => {
        options.finalBgmPlan.value = value
      },
      '背景音乐和音效处理方式',
    ))
    register(buildTextField(
      'final.tts_script',
      '终版 TTS 文案',
      options.finalTtsScript.value,
      (value) => {
        options.finalTtsScript.value = value
      },
      '用于直接生成旁白音频的正文文案',
    ))

    return {
      stage: 'final',
      stageLabel: options.currentStageLabel,
      currentFocus: '终版音频工作台与交付动作区',
      fields: Array.from(bindings.values()).map((item) => item.field),
      applyActions: (actions) => applyStageAssistantActions(bindings, actions),
    }
  }

  register(buildSelectField(
    'storyboard.workspace_tab',
    '故事板工作区标签',
    options.storyboardWorkspaceTab.value,
    [
      { label: '分镜脚本', value: 'script' },
      { label: '一致性资产', value: 'consistency' },
      { label: '镜头生成', value: 'generation' },
    ],
    (value) => {
      options.storyboardWorkspaceTab.value = value as StoryboardWorkspaceTab
    },
    '决定当前在分镜脚本、一致性资产还是镜头生成视图工作',
  ))
  register(buildSelectField(
    'storyboard.generation_tab',
    '镜头生成模式',
    options.storyboardGenerationTab.value,
    [
      { label: '图片生成', value: 'image' },
      { label: '视频生成', value: 'video' },
    ],
    (value) => {
      options.storyboardWorkspaceTab.value = 'generation'
      options.storyboardGenerationTab.value = value as StoryboardGenerationTab
    },
    '镜头生成区当前是在图片还是视频子模式',
  ))
  register(buildSelectField(
    'storyboard.consistency_tab',
    '一致性资产类型',
    options.consistencyAssetTab.value,
    [
      { label: '角色', value: 'character' },
      { label: '场景', value: 'scene' },
      { label: '道具', value: 'prop' },
    ],
    (value) => {
      options.storyboardWorkspaceTab.value = 'consistency'
      options.consistencyAssetTab.value = value as ConsistencyAssetType
    },
    '一致性资产页当前展示的资产类型',
  ))

  if (options.selectedStoryboardSceneNumber.value) {
    register(buildNumberField(
      'storyboard.selected_scene_number',
      '当前聚焦镜头',
      options.selectedStoryboardSceneNumber.value,
      (value) => {
        options.selectedStoryboardSceneNumber.value = Math.max(1, Math.round(value))
      },
      '当前正在编辑或生成的镜头序号',
    ))
  }

  const relevantScenes = getRelevantStoryboardScenes(options)
  relevantScenes.forEach((scene) => {
    const sceneNumber = scene.scene_number

    register(buildTextField(
      `storyboard.scene.${sceneNumber}.location`,
      `镜头 ${sceneNumber} 标题或地点`,
      String(scene.location || ''),
      (value) => {
        options.storyboardWorkspaceTab.value = 'script'
        options.selectedStoryboardSceneNumber.value = sceneNumber
        options.storyboardEditingSceneNumber.value = sceneNumber
        scene.location = value
        options.markStoryboardDraftDirty()
      },
      `分镜脚本里镜头 ${sceneNumber} 的标题或地点`,
      'text',
    ))
    register(buildTextField(
      `storyboard.scene.${sceneNumber}.shot_type`,
      `镜头 ${sceneNumber} 景别`,
      String(scene.shot_type || ''),
      (value) => {
        options.storyboardWorkspaceTab.value = 'script'
        options.selectedStoryboardSceneNumber.value = sceneNumber
        options.storyboardEditingSceneNumber.value = sceneNumber
        scene.shot_type = value
        options.markStoryboardDraftDirty()
      },
      `分镜脚本里镜头 ${sceneNumber} 的景别`,
      'text',
    ))
    register(buildTextField(
      `storyboard.scene.${sceneNumber}.camera_angle`,
      `镜头 ${sceneNumber} 机位角度`,
      String(scene.camera_angle || ''),
      (value) => {
        options.storyboardWorkspaceTab.value = 'script'
        options.selectedStoryboardSceneNumber.value = sceneNumber
        options.storyboardEditingSceneNumber.value = sceneNumber
        scene.camera_angle = value
        options.markStoryboardDraftDirty()
      },
      `分镜脚本里镜头 ${sceneNumber} 的机位角度`,
      'text',
    ))
    register(buildTextField(
      `storyboard.scene.${sceneNumber}.camera_movement`,
      `镜头 ${sceneNumber} 运镜方式`,
      String(scene.camera_movement || ''),
      (value) => {
        options.storyboardWorkspaceTab.value = 'script'
        options.selectedStoryboardSceneNumber.value = sceneNumber
        options.storyboardEditingSceneNumber.value = sceneNumber
        scene.camera_movement = value
        options.markStoryboardDraftDirty()
      },
      `分镜脚本里镜头 ${sceneNumber} 的运镜方式`,
      'text',
    ))
    register(buildTextField(
      `storyboard.scene.${sceneNumber}.description`,
      `镜头 ${sceneNumber} 场景描述`,
      String(scene.description || ''),
      (value) => {
        options.storyboardWorkspaceTab.value = 'script'
        options.selectedStoryboardSceneNumber.value = sceneNumber
        options.storyboardEditingSceneNumber.value = sceneNumber
        scene.description = value
        options.markStoryboardDraftDirty()
      },
      `分镜脚本里镜头 ${sceneNumber} 的场景描述`,
    ))
    register(buildTextField(
      `storyboard.scene.${sceneNumber}.action`,
      `镜头 ${sceneNumber} 画面描述`,
      String(scene.action || ''),
      (value) => {
        options.storyboardWorkspaceTab.value = 'script'
        options.selectedStoryboardSceneNumber.value = sceneNumber
        options.storyboardEditingSceneNumber.value = sceneNumber
        scene.action = value
        options.markStoryboardDraftDirty()
      },
      `分镜脚本里镜头 ${sceneNumber} 的动作和画面描述`,
    ))
    register(buildTextField(
      `storyboard.scene.${sceneNumber}.dialogue_summary`,
      `镜头 ${sceneNumber} 对白摘要`,
      String(scene.dialogue_summary || ''),
      (value) => {
        options.storyboardWorkspaceTab.value = 'script'
        options.selectedStoryboardSceneNumber.value = sceneNumber
        options.storyboardEditingSceneNumber.value = sceneNumber
        scene.dialogue_summary = value
        options.markStoryboardDraftDirty()
      },
      `分镜脚本里镜头 ${sceneNumber} 的对白`,
    ))
    register(buildTextField(
      `storyboard.scene.${sceneNumber}.background_prompt`,
      `镜头 ${sceneNumber} 图片创意描述`,
      String(scene.background_prompt || ''),
      (value) => {
        options.storyboardWorkspaceTab.value = 'generation'
        options.storyboardGenerationTab.value = 'image'
        options.selectedStoryboardSceneNumber.value = sceneNumber
        scene.background_prompt = value
        options.markStoryboardDraftDirty()
      },
      `镜头 ${sceneNumber} 的图片生成提示词`,
    ))
    register(buildTextField(
      `storyboard.scene.${sceneNumber}.video_prompt`,
      `镜头 ${sceneNumber} 视频创意描述`,
      String(scene.video_prompt || ''),
      (value) => {
        options.storyboardWorkspaceTab.value = 'generation'
        options.storyboardGenerationTab.value = 'video'
        options.selectedStoryboardSceneNumber.value = sceneNumber
        scene.video_prompt = value
        options.markStoryboardDraftDirty()
      },
      `镜头 ${sceneNumber} 的视频生成提示词`,
    ))
    register(buildNumberField(
      `storyboard.scene.${sceneNumber}.duration`,
      `镜头 ${sceneNumber} 视频时长`,
      Number(scene.duration || 5),
      (value) => {
        options.storyboardWorkspaceTab.value = 'generation'
        options.storyboardGenerationTab.value = 'video'
        options.selectedStoryboardSceneNumber.value = sceneNumber
        scene.duration = Math.min(15, Math.max(2, Math.round(value)))
        options.markStoryboardDraftDirty()
      },
      `镜头 ${sceneNumber} 的视频时长，范围 2 到 15 秒`,
    ))
  })

  if (options.storyboardWorkspaceTab.value === 'consistency') {
    getRelevantConsistencyAssets(options).forEach((asset) => {
      const assetType = asset.type
      register(buildTextField(
        `storyboard.asset.${assetType}.${asset.id}.name`,
        `${assetType === 'character' ? '角色' : assetType === 'scene' ? '场景' : '道具'}资产 ${asset.name || asset.id} 名称`,
        String(asset.name || ''),
        (value) => {
          const target = getAssetByKey(options.consistencyAssets.value, assetType, asset.id)
          if (!target) {
            return
          }
          options.storyboardWorkspaceTab.value = 'consistency'
          options.consistencyAssetTab.value = assetType
          target.name = value
          options.markConsistencyDirty()
        },
        `${assetType} 官方资产名称`,
        'text',
      ))
      register(buildTextField(
        `storyboard.asset.${assetType}.${asset.id}.prompt`,
        `${assetType === 'character' ? '角色' : assetType === 'scene' ? '场景' : '道具'}资产 ${asset.name || asset.id} 设定文本`,
        String(asset.prompt || ''),
        (value) => {
          const target = getAssetByKey(options.consistencyAssets.value, assetType, asset.id)
          if (!target) {
            return
          }
          options.storyboardWorkspaceTab.value = 'consistency'
          options.consistencyAssetTab.value = assetType
          target.prompt = value
          options.markConsistencyDirty()
        },
        `${assetType} 官方资产设定文本`,
      ))
    })
  }

  return {
    stage: 'storyboard',
    stageLabel: options.currentStageLabel,
    currentFocus: `${buildStoryboardCurrentFocus(options)}，优先直接修改分镜文本、提示词或资产设定，不要改审批意见`,
    fields: Array.from(bindings.values()).map((item) => item.field),
    applyActions: (actions) => applyStageAssistantActions(bindings, actions),
  }
}

function buildStoryboardCurrentFocus(options: BuildStageAssistantRegistryOptions) {
  const sceneNumber = options.selectedStoryboardSceneNumber.value
  const sceneLabel = sceneNumber ? `，当前镜头 ${sceneNumber}` : ''
  if (options.storyboardWorkspaceTab.value === 'script') {
    return `分镜脚本编辑区${sceneLabel}`
  }
  if (options.storyboardWorkspaceTab.value === 'consistency') {
    return `一致性资产编辑区，当前资产分类 ${options.consistencyAssetTab.value}`
  }
  return `镜头生成编辑区${sceneLabel}，当前模式 ${options.storyboardGenerationTab.value}`
}

function applyStageAssistantActions(
  bindings: Map<string, StageAssistantFieldBinding>,
  actions: WorkflowStageAssistantAction[],
): StageAssistantApplySummary {
  const applied: StageAssistantApplySummary['applied'] = []
  const skipped: StageAssistantApplySummary['skipped'] = []

  actions.forEach((action) => {
    const binding = bindings.get(action.target_key)
    if (!binding) {
      skipped.push({ key: action.target_key, label: action.target_key })
      return
    }

    const success = binding.apply(action.value)
    if (success) {
      applied.push({ key: action.target_key, label: binding.field.label })
      return
    }

    skipped.push({ key: action.target_key, label: binding.field.label })
  })

  return { applied, skipped }
}